import {useCallback, useEffect, useRef, useState} from 'react';
import { getAuthCookie, setAuthCookie } from 'utils/auth';
import { refreshAuthToken } from 'service/cognitoService';
import jwtDecode from 'jwt-decode';
import { reportError } from 'utils/reporting';
import { useSelector } from 'react-redux';
import { selectUser } from 'redux/user/selectors';

const MIN_TOKEN_TIME_TO_EXPIRE = 5 * 60 * 1000;

export const useRefreshToken = () => {
  const [, rescheduleRefresh] = useState();
  const timeout = useRef();
  const companyCodeRef = useRef();
  const auth = getAuthCookie();

  const user = useSelector(selectUser);
  const userCompanyCode = user?.Company?.code;

  const refreshToken = useCallback(
    async () => {
      const authTokens = await refreshAuthToken(auth.refresh_token);
      setAuthCookie({
        ...auth,
        ...authTokens,
      });
      rescheduleRefresh(Math.random());
    },
    [auth],
  );

  useEffect(() => {
    if (auth?.id_token && userCompanyCode) {
      try {
        const decoded = jwtDecode(auth?.id_token);
        const { exp, tid } = decoded;

        if (tid && userCompanyCode !== tid) {
          if (companyCodeRef.current === userCompanyCode) {
            reportError(new Error(
              `Token refresh did not resolve company code mismatch. Company code: ${userCompanyCode}, token tid: ${tid}`
            ));
          } else {
            companyCodeRef.current = userCompanyCode;
            refreshToken();
          }
          return;
        }

        const timeToRefreshToken = (exp * 1000 - new Date().getTime() - MIN_TOKEN_TIME_TO_EXPIRE);
        if (timeout.current) {
          clearTimeout(timeout.current);
        }

        if (timeToRefreshToken > 0) {
          timeout.current = setTimeout(
            refreshToken,
            timeToRefreshToken,
          );
        }
      } catch (e) {
        reportError(e);
      }
    }

    return () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
    };
  }, [auth, refreshToken, userCompanyCode]);

  return {
    refreshToken,
  }
};
